import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '../../reduxHooks';
import { Redirect } from 'react-router-dom';
import { Alert, Button, Card } from '@betareader/design-system';

import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { featureToggleUtil } from '../../utils';
import { McpGrant, listMcpGrants, revokeMcpGrant } from '../../services/mcpApi';

export const isMcpFeatureAvailable = (
  flags: Record<string, boolean> | undefined,
  flagsLoading: boolean,
  userProfile: any
): boolean => {
  if (flagsLoading) return false;
  if (!userProfile) return false;
  if (!featureToggleUtil.hasFeatureAccess(userProfile, 'mcp_api')) return false;
  return Boolean(
    flags?.mcp_api === true || flags?.mcp_api_beta_access_allowed === true
  );
};

type IntegrationsViewProps = {
  eligible: boolean;
  loading: boolean;
  grants: McpGrant[];
  revokingId: string | null;
  error?: string;
  onRevoke: (grantId: string) => void;
};

const scopeLabel = (scope: string): string =>
  scope === 'messages:read'
    ? 'Read messages'
    : scope === 'messages:write'
      ? 'Prepare reply drafts'
      : scope === 'admin:usage:read'
        ? 'Admin analytics'
        : scope === 'admin:moderation:read'
          ? 'Read moderation cases'
          : scope === 'admin:moderation:write'
            ? 'Moderation decisions'
            : scope;

export const IntegrationsView: React.FC<IntegrationsViewProps> = ({
  eligible,
  loading,
  grants,
  revokingId,
  error,
  onRevoke,
}) => {
  const { t } = useTranslation();
  return (
    <Card>
      <h1>{t('McpIntegrations', 'Integrations')}</h1>
      <p>
        {t(
          'McpIntegrationsDescription',
          'Connect OpenAI, Anthropic, or another compatible MCP client to your BetaReader messages.'
        )}
      </p>
      <Alert variant="info">
        {t(
          'McpNoManuscriptAccess',
          'Connected apps cannot access manuscript text through MCP.'
        )}
      </Alert>
      {!eligible && (
        <Alert variant="warning">
          {t(
            'McpUnavailable',
            'MCP integrations are available to eligible paid account owners and administrators.'
          )}
        </Alert>
      )}
      {error && <Alert variant="error">{error}</Alert>}
      {loading && <p role="status">{t('Loading', 'Loading…')}</p>}
      {!loading && grants.length === 0 && (
        <p>{t('McpNoConnectedApps', 'No connected apps.')}</p>
      )}
      {grants.length > 0 && (
        <ul
          aria-label={t('McpConnectedApps', 'Connected apps')}
          style={{ listStyle: 'none', padding: 0 }}
        >
          {grants.map(grant => (
            <li
              key={grant.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                padding: '12px 0',
                borderTop: '1px solid var(--br-border)',
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>{grant.clientName}</strong>
                <span style={{ display: 'block', color: 'var(--br-muted)' }}>
                  {grant.scopes
                    .map(scope =>
                      t(`McpScopeShort_${scope}`, scopeLabel(scope))
                    )
                    .join(', ')}
                  {grant.lastUsedAt
                    ? ` · ${t('McpLastUsed', 'Last used')}: ${new Date(
                        grant.lastUsedAt
                      ).toLocaleString()}`
                    : ''}
                </span>
              </span>
              <Button
                type="button"
                variant="danger"
                aria-busy={revokingId === grant.id}
                disabled={revokingId !== null}
                onClick={() => onRevoke(grant.id)}
              >
                {t('McpRevoke', 'Revoke')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export const canManageMcpForAccount = (
  userProfile: any,
  activeAccount: any
): boolean => {
  return Boolean(
    canOpenMcpIntegrationSettings(userProfile, activeAccount) &&
      activeAccount.product?.features?.canUseMcp === true
  );
};

/**
 * Account role gate only. Callers must also require the personal mcp_api
 * feature flag before showing any MCP UI. Grant creation/use is always
 * re-authorized by the API's paid entitlement and feature-gate checks.
 */
export const canOpenMcpIntegrationSettings = (
  userProfile: any,
  activeAccount: any
): boolean => {
  if (!userProfile || !activeAccount) return false;
  const ownerId = activeAccount.owner?._id ?? activeAccount.owner;
  const membership = userProfile.accountMembership?.find(
    (entry: any) => (entry.account?._id ?? entry.account) === activeAccount._id
  );
  return ownerId === userProfile._id || membership?.role === 'admin';
};

const Integrations: React.FC = () => {
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const activeAccount = useSelector(
    (state: any) => state.account.activeAccount
  );
  const { flags, isLoading: flagsLoading } = useFeatureFlags();
  const featureEnabled = isMcpFeatureAvailable(
    flags,
    flagsLoading,
    userProfile
  );
  const canOpenSettings =
    (featureEnabled &&
      canOpenMcpIntegrationSettings(userProfile, activeAccount)) ||
    (userProfile?.isSuperAdmin === true &&
      (activeAccount?.owner?._id ?? activeAccount?.owner) === userProfile?._id);
  const eligible =
    canOpenSettings && canManageMcpForAccount(userProfile, activeAccount);
  const [grants, setGrants] = useState<McpGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let current = true;
    if (!canOpenSettings) {
      setGrants([]);
      setLoading(false);
      return () => {
        current = false;
      };
    }
    setLoading(true);
    listMcpGrants()
      .then(value => {
        if (current) setGrants(value);
      })
      .catch(reason => {
        if (current) setError(reason.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [canOpenSettings, userProfile?._id]);

  const revoke = async (grantId: string) => {
    setRevokingId(grantId);
    setError(undefined);
    try {
      await revokeMcpGrant(grantId);
      setGrants(current => current.filter(grant => grant.id !== grantId));
    } catch (reason: any) {
      setError(reason.message);
    } finally {
      setRevokingId(null);
    }
  };

  if (flagsLoading || !userProfile) return <p role="status">Loading…</p>;
  if (!canOpenSettings) return <Redirect to="/account/settings" />;

  return (
    <IntegrationsView
      eligible={eligible}
      loading={loading}
      grants={grants}
      revokingId={revokingId}
      error={error}
      onRevoke={revoke}
    />
  );
};

export default Integrations;
